'use client';
import type React from 'react';
import {
  cloneElement,
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useInView } from 'react-intersection-observer';
import { v4 as uuid } from 'uuid';
import { useClientOnlyConstant } from '../../hooks/useClientOnlyConstant';
import { useShoppingFinishedId } from '../../hooks/useShoppingFinishedId';
import { EventName, type ShoppingFinishedPayload, type SkuViewedPayload } from '../../Shibuya';
import { useAnalytics } from '..';
import { useAnalyticsPageView } from '../AnalyticsPageView';
import { getCleanLocationHref } from '../urlUtils';

type DataLayerEvent = {
  event: string;
  [key: string]: unknown;
};
declare global {
  interface Window {
    dataLayer?: DataLayerEvent[];
  }
}

export interface AnalyticsSkuViewedContextValue {
  skuImpressionId: string;
  shoppingFinishedId: string;
  skuViewedPayload?: SkuViewedPayload;
  firePartnerCtaClickEvent: (props: AnalyticsShoppingFinishedCallbackProps) => void;
  fireShoppingFinishedEvent: (props: AnalyticsShoppingFinishedCallbackProps) => void;
  fireShoppingFinishedEventAsync: (props: AnalyticsShoppingFinishedCallbackProps) => Promise<void>;
}

export interface AnalyticsShoppingFinishedCallbackProps {
  destinationUrl: ShoppingFinishedPayload['destination_url'];
  ctaType?: ShoppingFinishedPayload['cta_type'];
  finishType?: ShoppingFinishedPayload['finish_type'];
  revenueAmount?: ShoppingFinishedPayload['revenue_amount'];
  revenueType?: ShoppingFinishedPayload['revenue_type'];
  interstitialClientBotSuspected?: ShoppingFinishedPayload['interstitial_client_bot_suspected'];
  monetizable?: SkuViewedPayload['monetizable'];
  overrideShoppingFinishedId?: ShoppingFinishedPayload['shopping_finish_id'];
  entryPointPageUrl?: SkuViewedPayload['entry_point_page_url'];
  additionalAttributes?: Record<string, unknown>;
}

const AnalyticsSkuViewedContext = createContext<AnalyticsSkuViewedContextValue | null>(null);

export interface AnalyticsSkuViewedProps {
  source: SkuViewedPayload['sku_source'];
  name: SkuViewedPayload['sku_name'];
  productType: SkuViewedPayload['sku_product_type'];
  level: SkuViewedPayload['sku_level'];
  institutionId?: SkuViewedPayload['institution_id'];
  institutionName?: SkuViewedPayload['institution_name'];
  productId?: SkuViewedPayload['product_id'];
  productName?: SkuViewedPayload['product_name'];
  offerId?: SkuViewedPayload['offer_id'];
  offerName?: SkuViewedPayload['offer_name'];
  monetizable: SkuViewedPayload['monetizable'];
  impressionSection: SkuViewedPayload['impression_section'];
  impressionPosition: SkuViewedPayload['impression_position'];
  children?: React.JSX.Element | ((context: AnalyticsSkuViewedContextValue) => React.JSX.Element);
  additionalAttributes?: Record<string, unknown>;
}

export const AnalyticsSkuViewed = forwardRef<HTMLElement, AnalyticsSkuViewedProps>(
  ({ children, ...props }, ref) => {
    const { analytics, deployableName, deployableVersion, deviceId, gitSha } = useAnalytics();
    const { pageViewId, pageName, pageViewReady, pageType } = useAnalyticsPageView();
    const [skuViewedPayload, setSkuViewedPayload] = useState<SkuViewedPayload>();
    const skuImpressionId = useClientOnlyConstant(() => uuid(), '');
    const shoppingFinishedId = useShoppingFinishedId();
    const { ref: inViewRef, inView } = useInView({
      root: null,
      rootMargin: '1px 0px 0px 0px',
      threshold: 0,
      triggerOnce: true,
    });

    const setRefs = useCallback(
      (node: HTMLElement | null) => {
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref && 'current' in ref) {
          ref.current = node;
        }
        inViewRef(node);
      },
      [inViewRef],
    );

    useEffect(() => {
      if (pageViewReady && deviceId && inView) {
        const eventPayload: SkuViewedPayload = {
          sku_id: props.offerId,
          sku_source: props.source,
          sku_name: props.name,
          sku_product_type: props.productType,
          sku_level: props.level,
          institution_id: props.institutionId,
          institution_name: props.institutionName,
          product_id: props.productId,
          product_name: props.productName,
          offer_id: props.offerId,
          offer_name: props.offerName,
          monetizable: props.monetizable,
          impression_page_name: pageName || window.location.pathname,
          impression_page_url: getCleanLocationHref(),
          impression_pageview_id: pageViewId,
          impression_section: props.impressionSection,
          impression_position: props.impressionPosition,
          sku_impression_id: skuImpressionId,
          deployable_name: deployableName,
          deployable_version: deployableVersion,
          traditional_device_id: deviceId,
          additional_attributes: {
            page_type: pageType,
            ...props.additionalAttributes,
          },
        };
        analytics?.track(EventName.SKU_VIEWED, eventPayload, {
          context: {
            app: {
              name: deployableName,
              version: gitSha,
            },
          },
        });
        window.dataLayer?.push({
          event: 'SkuViewed',
          ...eventPayload,
        });
        setSkuViewedPayload(eventPayload);
      }
    }, [pageViewReady, deviceId, inView]);

    const fireShoppingFinishedEvent = useCallback(
      ({
        destinationUrl,
        ctaType,
        finishType = 'external_application',
        monetizable = skuViewedPayload?.monetizable,
        interstitialClientBotSuspected,
        overrideShoppingFinishedId,
        revenueAmount,
        revenueType,
        entryPointPageUrl,
        additionalAttributes = {},
      }: AnalyticsShoppingFinishedCallbackProps) => {
        if (inView && skuViewedPayload) {
          const eventPayload: ShoppingFinishedPayload = {
            ...skuViewedPayload,
            monetizable: monetizable,
            destination_url: destinationUrl,
            cta_type: ctaType?.toLowerCase(),
            finish_type: finishType,
            interstitial_client_bot_suspected: interstitialClientBotSuspected,
            revenue_amount: revenueAmount,
            revenue_type: revenueType,
            shopping_finish_id: overrideShoppingFinishedId ?? shoppingFinishedId,
            entry_point_page_url: entryPointPageUrl,
            additional_attributes: {
              ...skuViewedPayload.additional_attributes,
              ...additionalAttributes,
            },
          };

          analytics?.track(EventName.SHOPPING_FINISHED, eventPayload, {
            context: {
              app: {
                name: deployableName,
                version: gitSha,
              },
            },
          });
          window.dataLayer?.push({
            event: 'ShoppingFinished',
            ...eventPayload,
          });
        }
      },
      [inView, skuViewedPayload],
    );
    const firePartnerCtaClickEvent = useCallback(
      ({
        destinationUrl,
        ctaType,
        finishType = 'external_application',
        monetizable = skuViewedPayload?.monetizable,
        interstitialClientBotSuspected,
        overrideShoppingFinishedId,
        revenueAmount,
        revenueType,
        entryPointPageUrl,
        additionalAttributes = {},
      }: AnalyticsShoppingFinishedCallbackProps) => {
        if (!skuViewedPayload) {
          return;
        }
        const eventPayload: ShoppingFinishedPayload = {
          ...skuViewedPayload,
          monetizable: monetizable,
          destination_url: destinationUrl,
          cta_type: ctaType?.toLowerCase(),
          finish_type: finishType,
          interstitial_client_bot_suspected: interstitialClientBotSuspected,
          revenue_amount: revenueAmount,
          revenue_type: revenueType,
          shopping_finish_id: overrideShoppingFinishedId ?? shoppingFinishedId,
          entry_point_page_url: entryPointPageUrl,
          additional_attributes: {
            ...skuViewedPayload.additional_attributes,
            ...additionalAttributes,
          },
        };

        // TODO: Segment Standardization Cleanup
        analytics?.track(EventName.PARTNER_CTA_CLICK, eventPayload, {
          context: {
            app: {
              name: deployableName,
              version: gitSha,
            },
          },
        });
        analytics?.track(EventName.PARTNER_CTA_CLICKED_CUSTOM, eventPayload, {
          context: {
            app: {
              name: deployableName,
              version: gitSha,
            },
          },
        });
      },
      [skuViewedPayload],
    );
    const fireShoppingFinishedEventAsync = useCallback(
      async ({
        destinationUrl,
        ctaType,
        finishType = 'external_application',
        monetizable = skuViewedPayload?.monetizable,
        interstitialClientBotSuspected,
        revenueAmount,
        revenueType,
        entryPointPageUrl,
        overrideShoppingFinishedId,
      }: AnalyticsShoppingFinishedCallbackProps) => {
        if (inView && skuViewedPayload) {
          const eventPayload: ShoppingFinishedPayload = {
            ...skuViewedPayload,
            monetizable: monetizable,
            destination_url: destinationUrl,
            cta_type: ctaType?.toLowerCase(),
            finish_type: finishType,
            interstitial_client_bot_suspected: interstitialClientBotSuspected,
            revenue_amount: revenueAmount,
            revenue_type: revenueType,
            shopping_finish_id: overrideShoppingFinishedId ?? shoppingFinishedId,
            entry_point_page_url: entryPointPageUrl,
          };
          if (analytics) {
            await analytics.track(EventName.SHOPPING_FINISHED, eventPayload, {
              context: {
                app: {
                  name: deployableName,
                  version: gitSha,
                },
              },
            });
          }
          window.dataLayer?.push({
            event: 'ShoppingFinished',
            ...eventPayload,
          });
        }
      },
      [inView, skuViewedPayload],
    );
    const context: AnalyticsSkuViewedContextValue = {
      skuImpressionId,
      shoppingFinishedId,
      skuViewedPayload,
      firePartnerCtaClickEvent,
      fireShoppingFinishedEvent,
      fireShoppingFinishedEventAsync,
    };
    const child = typeof children === 'function' ? children(context) : children;
    if (!child) {
      throw new Error('Missing Child');
    }
    return (
      <AnalyticsSkuViewedContext.Provider value={context}>
        {cloneElement(child, {
          ref: setRefs,
        })}
      </AnalyticsSkuViewedContext.Provider>
    );
  },
);

export const useAnalyticsSkuViewedContext = () => {
  const context = useContext(AnalyticsSkuViewedContext);
  if (!context) {
    throw new Error('useAnalyticsSkuViewedContext must be used within AnalyticsSkuViewed');
  }
  return context;
};
